import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, TrendingUp, AlertTriangle, Wand2, Loader2, Users, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function SkillRow({ s, tone, onRemediate, busy }) {
  const good = tone === "good";
  return (
    <div className="rounded-2xl border border-[#E5DECF] bg-white px-4 py-3" data-testid={`insight-skill-${s.subject_id}-${s.skill.replace(/\W+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.subject_color }} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-heading text-sm font-bold text-[#1F2937]">{s.skill}</div>
          <div className="text-[11px] text-[#6B7280]">{s.subject_name}{s.grade ? ` · ${s.grade}` : ""} · {s.students} student{s.students === 1 ? "" : "s"}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="h-2 w-20 overflow-hidden rounded-full bg-[#F2ECE1] sm:w-24"><div className="h-full rounded-full" style={{ width: `${s.avg_score}%`, background: good ? "#10B981" : "#E05A47" }} /></div>
          <span className={`w-11 text-right font-heading text-sm font-extrabold ${good ? "text-[#047857]" : "text-[#B4402F]"}`} data-testid="insight-skill-score">{s.avg_score}%</span>
        </div>
      </div>
      {!good && (
        <button onClick={() => onRemediate(s)} disabled={!!busy} data-testid={`remediate-${s.subject_id}-${s.skill.replace(/\W+/g, "-").toLowerCase()}`}
          className="mt-2.5 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[#7A3FF2] px-3 py-2 text-xs font-heading font-bold text-white hover:bg-[#652BDB] disabled:opacity-50 sm:w-auto">
          {busy === s.skill ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} {busy === s.skill ? "Building remediation lesson…" : "Generate Remediation Lesson"}
        </button>
      )}
    </div>
  );
}

export default function ClassInsightsCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.classInsights().then(setData).catch(() => setData({ students_total: 0, strengths: [], needs_attention: [], skills: [] }));
  useEffect(() => { load(); }, []);

  if (!data || data.students_total === 0) return null;

  const remediate = async (s) => {
    setBusy(s.skill);
    try {
      const { draft } = await api.remediationDraft({ subject_id: s.subject_id, grade: s.grade || "Grade 1", skill: s.skill, lesson_ids: s.lessons.map((l) => l.id), avg_score: s.avg_score });
      sessionStorage.setItem("zl_pending_draft", JSON.stringify({ draft, subject_id: s.subject_id, grade: s.grade || "Grade 1", skill: s.skill }));
      toast.success(`Remediation lesson for "${s.skill}" is ready — review and assign.`);
      navigate("/lessons");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const empty = data.skills.length === 0;
  return (
    <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-5 sm:p-6" data-testid="class-insights-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#7A3FF2]/10 text-[#7A3FF2]"><BarChart3 size={22} /></div>
          <div>
            <div className="font-heading text-lg font-extrabold text-[#1F2937]">Class Insights</div>
            <p className="font-body text-xs text-[#6B7280]"><Users size={12} className="mr-1 inline" />{data.students_active} of {data.students_total} students have practiced · skill averages from real attempts</p>
          </div>
        </div>
        <button onClick={load} data-testid="insights-refresh" className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#6B7280] hover:bg-[#F2ECE1]"><RefreshCw size={13} /> Refresh</button>
      </div>

      {empty ? (
        <p className="mt-4 rounded-2xl border border-dashed border-[#E5DECF] bg-[#FAF7F2] p-5 text-center font-body text-sm text-[#94A3B8]" data-testid="insights-empty">Insights appear here once your students complete a few activities.</p>
      ) : (
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <div>
            <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#047857]"><TrendingUp size={14} /> Class strengths</div>
            <div className="space-y-2" data-testid="insights-strengths">
              {data.strengths.map((s) => <SkillRow key={s.subject_id + s.skill} s={s} tone="good" />)}
              {data.strengths.length === 0 && <p className="font-body text-xs text-[#94A3B8]">No skills at 80%+ yet — keep practicing!</p>}
            </div>
          </div>
          <div>
            <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#B4402F]"><AlertTriangle size={14} /> Needs attention</div>
            <div className="space-y-2" data-testid="insights-needs">
              {data.needs_attention.map((s) => <SkillRow key={s.subject_id + s.skill} s={s} tone="bad" onRemediate={remediate} busy={busy} />)}
              {data.needs_attention.length === 0 && <p className="font-body text-xs text-[#94A3B8]" data-testid="insights-needs-empty">Nothing under 70% — the class is on track.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
